import React, { useState } from 'react';
import { Upload, Send, Lightbulb } from 'lucide-react';
import { cn } from '../../utils/classSupport';
import { coverNameToID } from '../../utils/Branch/SupportBranch'

export default function AddBugForm({ onAddBug, type, initialData = null }) {
    const [formData, setFormData] = useState(initialData || {
        branch: '',
        personFeedback: '',
        image: '',
        content: '',
        priority: 'Medium',
        status: 'Submitted',
        notes: '',
        personHandling: '',
        deadline: '',
        project: 'Database',
    });
    const [image, setImage] = useState(initialData?.image || "");

    const handleImageChange = (e) => {
        e.preventDefault();

        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setImage(reader.result);
            setFormData(prev => ({ ...prev, image: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    React.useEffect(() => {
        if (initialData) {
            setFormData({
                branchID: initialData.branchID || '',
                personFeedback: initialData.personHandling || '',
                image: initialData.image || '',
                content: initialData.content || '',
                priority: initialData.priority || 'Medium',
                status: initialData.status || 'Submitted',
                notes: initialData.notes || '',
                personHandling: initialData.personHandling || '',
                deadline: initialData.deadline || '',
                project: initialData.project || 'Other',
                type: type
            });
        }
    }, [initialData]);

    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImage(reader.result);
                    setFormData(prev => ({ ...prev, image: reader.result }));
                };
                reader.readAsDataURL(blob);
                break;
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onAddBug(formData);
        setFormData({ 
            branchID: '', 
            personFeedback: '', 
            image: '', 
            content: '', 
            status: 'Submitted',
            notes: '',
            personHandling: '',
            deadline: '',
            type
        });
        setImage("");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div style={{
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
            <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '25px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#2c3e50'
            }}>
                {type === 'idea' ? (
                    <Lightbulb className="w-5 h-5" style={{color: '#f39c12'}} />
                ) : (
                    <Send className="w-5 h-5" style={{color: '#3498db'}} />
                )}
                {type === 'idea' ? 'Add New Idea' : 'Add Bug Feedback'}
            </h2>
            <form onSubmit={handleSubmit} onPaste={handlePaste} style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                <div style={{display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center'}}>
                    <label style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: '#2c3e50',
                        minWidth: '200px',
                        marginBottom: 0
                    }}>
                        Person Providing Feedback
                    </label>
                    <input
                        type="text"
                        name="personFeedback"
                        value={formData.personFeedback}
                        onChange={handleChange}
                        required
                        style={{
                            padding: '12px 15px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease',
                            fontFamily: 'inherit',
                            flex: 1
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3498db'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                        placeholder="Your Name"
                    />
                </div>

                <div style={{display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center'}}>
                    <label style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: '#2c3e50',
                        minWidth: '200px',
                        marginBottom: 0
                    }}>
                        Project with taget
                    </label>
                    <select
                        name="project"
                        value={formData.project}
                        onChange={handleChange}
                        style={{
                            padding: '12px 15px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease',
                            fontFamily: 'inherit',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            flex: 1
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3498db'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    >
                        <option value="Database">Database</option>
                        <option value="Report">Report</option>
                        <option value="Mail">Mail</option>
                        <option value="Web">Web</option>
                        <option value="Application">Application</option>
                        <option value="Idea">Idea</option>
                        <option value="Other">Other</option>
                    </select>
                </div>

                <div style={{display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start'}}>
                    <label style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: '#2c3e50',
                        minWidth: '200px',
                        paddingTop: '12px',
                        marginBottom: 0
                    }}>
                        Attached Image
                    </label>
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            style={{
                                padding: '12px 15px',
                                borderRadius: '8px',
                                border: '2px solid #e0e0e0',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease',
                                fontFamily: 'inherit',
                                cursor: 'pointer'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3498db'}
                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                        />
                        <p style={{
                            fontSize: '0.85rem',
                            color: '#7f8c8d',
                            margin: '5px 0 10px 0',
                            fontStyle: 'italic'
                        }}>
                            💡 Tip: You can also paste images directly (Ctrl+V) into this form
                        </p>
                        {image && (
                            <div style={{
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '2px solid #3498db',
                                maxWidth: '300px'
                            }}>
                                <img 
                                    src={image} 
                                    alt="preview" 
                                    style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        display: 'block'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    <label style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: '#2c3e50'
                    }}>
                        Content / Description
                    </label>
                    <textarea
                        name="content"
                        value={formData.content}
                        onChange={handleChange}
                        required
                        rows={5}
                        style={{
                            padding: '12px 15px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            minHeight: '140px'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3498db'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                        placeholder="Describe the bug..."
                    />
                </div>

                <button
                    type="submit"
                    style={{
                        padding: '12px 30px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                        color: 'white',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)',
                        alignSelf: 'center',
                        minWidth: '200px'
                    }}
                    onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(52, 152, 219, 0.4)';
                    }}
                    onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(52, 152, 219, 0.3)';
                    }}
                >
                    {initialData ? '✎ Update Bug' : type === 'idea' ? '💡 Submit Idea' : '📝 Submit Report'}
                </button>
            </form>
        </div>
    );
}
